import { useCallback, useContext, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  CREATE_SOURCE_FAILED_TO_CREATE_ERROR_HEADER,
  CREATE_SOURCE_PERMISSION_DENIED_HEADER,
  CREATE_SOURCE_PERMISSION_DENIED_SUBHEADER,
  CREATE_SOURCE_UNEXPECTED_ERROR,
} from "@/pages/sources/components/create/sources/constants";
import { useFileUploader } from "@/pages/sources/components/create/sources/hooks";
import { TextActionType } from "@/pages/sources/components/create/sources/text/actions";
import {
  SourceTextContext,
  SourceTextContextShape,
} from "@/pages/sources/components/create/sources/text/SourceTextProvider";
import { TextPhase } from "@/pages/sources/components/create/sources/text/types";
import {
  buildCreateSourceTextRequest,
  extractProvisioningResult,
  getCreateSourceTextContentError,
  textToFile,
} from "@/pages/sources/components/create/sources/text/utils";
import {
  getCreateSourceNameError,
  isCreateSourceValueProvided,
} from "@/pages/sources/components/create/sources/utils";

import { ToastVariant } from "@/providers/toast/Toast";
import { useToast } from "@/providers/toast/useToast";

import { useCreateSourceMutation } from "@/api/mutations/sources";
import { createListSourcesInfiniteQueryKey } from "@/api/queries/sources";
import { isPermissionError } from "@/api/utils";

import { normalizeToAlphanumeric } from "@/utils/text";

export function useSourceTextContext(): SourceTextContextShape {
  const context = useContext(SourceTextContext);
  if (!context) {
    throw new Error("useSourceTextContext must be used within a SourceTextProvider");
  }
  return context;
}

interface UseSourceTextOptions {
  onSuccess?: (sourceId: string) => void;
}

export function useSourceText({ onSuccess }: UseSourceTextOptions) {
  const { state, dispatch } = useSourceTextContext();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { mutateAsync: createSourceMutation } = useCreateSourceMutation();

  const fileName = useMemo(
    () => `${normalizeToAlphanumeric(state.sourceName)}.txt`,
    [state.sourceName],
  );

  // Convert text content to File for the shared uploader
  const file = useMemo(
    () => textToFile(state.textContent, fileName),
    [state.textContent, fileName],
  );

  const { uploadSyncFiles } = useFileUploader({
    files: [file],
  });

  const nameError = useMemo(() => getCreateSourceNameError(state), [state]);
  const textContentError = useMemo(() => getCreateSourceTextContentError(state), [state]);
  const isDisabled = state.phase === TextPhase.IN_PROGRESS || state.phase === TextPhase.COMPLETED;

  const save = useCallback(async () => {
    dispatch({ type: TextActionType.SET_SHOULD_SHOW_ERRORS, payload: true });

    if (
      state.phase === TextPhase.IN_PROGRESS ||
      !isCreateSourceValueProvided(state.sourceName) ||
      !isCreateSourceValueProvided(state.textContent)
    ) {
      return;
    }

    try {
      dispatch({ type: TextActionType.SET_PHASE, payload: TextPhase.IN_PROGRESS });

      const createRequest = buildCreateSourceTextRequest(
        state.sourceName,
        state.textContent,
        fileName,
      );
      const createSourceResponse = await createSourceMutation(createRequest);
      const provisioningResult = extractProvisioningResult(createSourceResponse);

      await uploadSyncFiles(provisioningResult.uploadSync);

      dispatch({ type: TextActionType.SET_PHASE, payload: TextPhase.COMPLETED });

      void queryClient.invalidateQueries({ queryKey: createListSourcesInfiniteQueryKey() });

      showToast({
        variant: ToastVariant.SUCCESS,
        header: "Source created",
        subheader: "The source has been created successfully",
      });
      if (onSuccess) onSuccess(provisioningResult.sourceId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : CREATE_SOURCE_UNEXPECTED_ERROR;
      dispatch({ type: TextActionType.SET_PHASE, payload: TextPhase.ERROR });
      dispatch({ type: TextActionType.SET_ERROR, payload: errorMessage });
      const isDenied = isPermissionError(error);
      showToast({
        variant: ToastVariant.ERROR,
        header: isDenied
          ? CREATE_SOURCE_PERMISSION_DENIED_HEADER
          : CREATE_SOURCE_FAILED_TO_CREATE_ERROR_HEADER,
        subheader: isDenied ? CREATE_SOURCE_PERMISSION_DENIED_SUBHEADER : errorMessage,
      });
    }
  }, [
    state.phase,
    state.sourceName,
    state.textContent,
    fileName,
    dispatch,
    createSourceMutation,
    uploadSyncFiles,
    queryClient,
    showToast,
    onSuccess,
  ]);

  return {
    state,
    dispatch,
    save,
    isDisabled,
    nameError,
    textContentError,
  };
}
